
import { Component, EventEmitter, Input, OnInit, OnChanges, Output, OnDestroy, ViewChild} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { cloneDeep, remove } from 'lodash';

import pattern from '../../../../assets/data/pattern.json';

import { ContextFactory } from '@shared/factories/context/context.factory';
import { GlobalCfgFactory } from '@shared/factories/global-cfg/global-cfg.factory';
import { UserPermisionsFactory } from '@shared/factories/user-permisions/user-permisions.factory';

import { CommonUtilsService } from '@shared/services/common-utils/common-utils.service';
import { ApiServiceService } from '@shared/services/api-service/api-service.service';
import { BroadcastService } from '@shared/services/broadcast/broadcast.service';
import { ConvertDatePipe } from '../../filters/convert-date/convert-date.pipe';
import { FieldTypeListMultipleComponent } from '../field-type-list/field-type-list-multiple/field-type-list-multiple.component';
import { FieldTypeListExtendedComponent } from '../field-type-list-extended/field-type-list-extended.component';
import { FieldTypeSwitchComponent } from '../field-type-switch/field-type-switch.component';
import { TooltipModule } from 'primeng/tooltip';
import { AfTranslateDirective } from '../../modules/translate/directive/af-translate.directive';
import { FieldTypeTimeComponent } from '../field-type-time/field-type-time.component';
import { FieldTypeDateComponent } from '../field-type-date/field-type-date.component';
import { FieldTypeContactsComponent } from '@shared/components/field-type-contacts/field-type-contacts.component';
import { FieldTypeTextSimpleComponent } from '../field-type-text/field-type-text-simple/field-type-text-simple.component';
import { FieldTypeListSimpleComponent } from '../field-type-list/field-type-list-simple/field-type-list-simple.component';
import { FormsModule } from '@angular/forms';
import { FieldTypeRichTextEditableComponent } from '../field-type-rich-text/field-type-rich-text-editable/field-type-rich-text-editable.component';
import { CustomLandingFactory } from '@shared/factories/custom-landing/custom-landing.factory';
import { AfTranslateFactory } from '@shared/modules/translate/factory/translate.factory';
import moment from 'moment';
import { DateConverterService } from '@shared/services/date-converter/date-converter.service';
import { ModalServiceService } from '@shared/services/modal-service/modal-service.service';
import { ConceptStateFactory } from '@shared/factories/concept-state/concept-state.factory';
import { AfTranslatePipe } from '@shared/modules/translate/pipe/af-translate.pipe';

@Component({
  selector: 'task-form',
  templateUrl: './task-form.component.html',
  standalone: true,
  imports: [
    FormsModule,
    FieldTypeListSimpleComponent,
    FieldTypeTextSimpleComponent,
    FieldTypeContactsComponent,
    FieldTypeDateComponent,
    FieldTypeTimeComponent,
    AfTranslateDirective,
    AfTranslatePipe,
    TooltipModule,
    FieldTypeSwitchComponent,
    FieldTypeListExtendedComponent,
    FieldTypeListMultipleComponent,
    ConvertDatePipe,
    FieldTypeRichTextEditableComponent
  ]
})
export class TaskFormComponent implements OnInit, OnChanges, OnDestroy {
  genericTask: any = this.route.snapshot.data['genericTask'];
  subscribers: any = {};
  isLanding: boolean = this.customLanding.isLanding();
  skin:string = this.globalCfg.skin;
  userPermissions:any = this.permissions.getPermissions();
  taskForm:any = this.configureTaskForm();
  taskInit: boolean = false;

 get isTaskClosed(): boolean {
   const stateId = this.taskForm?.task?.taskStateId;
   return stateId === 5 || stateId === 6;
 }

  constructor(private route: ActivatedRoute,
    private globalCfg: GlobalCfgFactory,
    private permissions: UserPermisionsFactory,
    private translate: AfTranslateFactory,
    private customLanding: CustomLandingFactory,
    private conceptState: ConceptStateFactory,
    private context: ContextFactory,
    private commonUtilsService: CommonUtilsService,
    private apiService: ApiServiceService,
    private broadcastService: BroadcastService,
    private dateConverterService: DateConverterService,
    private modalService: ModalServiceService ) {}

  @Input() concept: any = null;
  @Input() task: any = null;
  @Input() shared: any = null;
  @Input() fromToConfiguration: any = {};

  @Output() cancelTask = new EventEmitter();
  @Output() createAlert = new EventEmitter();
  @Output() launchAction = new EventEmitter();
  @ViewChild(FieldTypeDateComponent) fieldTypeDateComponent: FieldTypeDateComponent;

  ngOnInit(){
    if(!this.context.user.userId){
      this.context.user.userId = this.context.user.contactId;
    }
    this.configureGenericTask();
    this.configureTaskInit();
    this.getPatterns();
  }

  ngOnChanges(): void {
    this.configureGenericTask();
    let that: any = this.taskForm;
    that.task = this.task;
    that.concept = this.concept;
    if(this.task && this.task.taskTypeId === 3){
      this.configureShareDocuments();
    }
    if(that.concept.conceptObjectId){
      this.apiService.get('documentobjects/allfilesids', this.concept.conceptObjectId)
      .then((data:any)=>{
        this.taskForm.shareDocumentationList = this.commonUtilsService.editList(data, 'documentObjectId', 'name');
      })
    }
  }

  configureGenericTask(){
    if(this.isLanding && this.task?.taskId && !this.task?.taskEvaluationId){
      this.genericTask = true;
    }
  }

  configureTaskForm(){
    return {
      showAccept: true,
      hasContacts: false,
      typeShareDocumentSelected: '0',
      saveTask: null,
      titleParticipants: 'task',
      taskDate: null,
      repetitionActived: false,
      description: false,
      canCreate: this.userPermissions !== 'none',
      descriptionField: { label: this.translate.instant('common.description'), fieldTypeId: 'rich' },
      taskDescription: {
        label: this.translate.instant('common.description')
      },
      taskReach: {
        required: true,
        label: this.translate.instant('task-edit.task-scope')
      },
      taskEvaluation: {
        required: true,
        label: this.translate.instant('common.implantation')
      },
      taskClient: {
        required: true,
        isTree: true,
        toFind: 'text',
        label: this.translate.instant('common.organization')
      },
      taskTitle: {
        required: true,
        maxLength: 250,
        label: this.translate.instant('common.title')
      },
      typeOfTask: {
        required: true,
        disabled:false,
        label: this.translate.instant('task-concept.task-type')
      },
      taskExpirationDate: {
        from:'task',
        label: this.translate.instant('task-edit.expiration-date'),
        dateLockType: 2,
      },
      stateStartTime: {
        label: this.translate.instant('task-form.hour-init'),
        required: true
      },
      stateEndTime: {
        label: this.translate.instant('task-form.hour-finish'),
        required: true
      },
      securitySettings: {
        type: 'securitysettings',
        model: 'Open',
        required: true,
        label: this.translate.instant('task-form.security-settings'),
      },
      taskPlace: {
        required:  true,
        label: this.translate.instant('task-edit.place'),
        text: this.translate.instant('task-edit.place')
      },
      repeatPeriod: {
        label: this.translate.instant('repeat-pattern.repeat-periodically'),
      },
      shareTypeDocumentField: {
        multiple: false,
        required: true,
        type: 'modal',
        label: this.translate.instant('share-document.type-share-label')
      },
      shareDocumentationField: {
        label: this.translate.instant('share-document.restricted-select'),
        multiple: true,
        required: true,
      },
      typeShareDocument: [{
          listValueId:'0',
          text: this.translate.instant('share-document.no-share-documents')
        },{
          listValueId:'1',
          text: this.translate.instant('share-document.share-all-documents')
        },{
          listValueId:'2',
          text: this.translate.instant('share-document.share-selected-documents')
      }],
      listOptionsReach: [
        {
          text: this.translate.instant('task-form.evaluation-task'),
          listValueId: 'Evaluation'
        },
        {
          text: this.translate.instant('task-form.client-task'),
          listValueId: 'Client'
        }
      ]
    }
  }

  configureTaskInit() {
    this.initTinyMce(false);
    this.configureTask();
    this.configureFields();
    this.getTaskTypes();
    if(this.genericTask){
      if(this.task.taskId){
        let clients : any = [{ clientId: this.task.clientId, name: this.task.client, active: true}];
        let evaluations : any = [{ evaluationId: this.task.evaluationId, name: this.task.evaluation}];
        this.getClients(clients);
        this.getEvaluations(evaluations);
      }else{
        this.getApiClients();
        this.getApiEvaluations();
      }
    }else if(this.taskForm.task.taskTypeId === 4){
      this.getApiClients();
      this.getApiEvaluations();
    }
    if(!this.isLanding){
      this.subscribers.saveTaskToExitModalBroadcast = this.broadcastService.subscribe('saveTaskToExitModal', (data:any)=>{
        this.taskForm.configurationFromExitModal = data;
        this.taskForm.configurationFromExitModal.fromExitModal = true;
        setTimeout(()=>{
          this.addOrEditTask();
        });
      });
    }
  }

  initTinyMce(hidetiny:any = false) {
    this.taskForm.descriptionField.noEditable = !this.taskForm.canCreate || hidetiny?true:false;
  }

  configureTask() {
    let that: any = this.taskForm;
    let task: any = that.task;
    if (task && task.taskId){
      that.newValueId = task.evaluationId;
      if (task.taskStateName !== undefined) {
        task.state = task.taskStateName;
      }
      if (task.participants && task.participants.length) {
        let size = task.participants.length - 1;
        that.participantsTask = task.participants[size].id;
      }
      if (task.expirationDate) {
        that.taskDate = this.dateConverterService.toDate(that.task.expirationDate);
      }
      if(task.taskTypeId === 7){
        that.taskReach.model = 'process-task';
      }else if(this.genericTask){
        if (task.clientId && !task.evaluationId) {
          that.taskReach.model = 'Client';
          that.newValueId = task.clientId;
        }else {
          that.taskReach.model = 'Evaluation';
        }
      }
    } else {
      that.userLogin = this.context.user.fullName;
    }

    that.taskParticipants = {
      typeConfiguration: that.taskReach.model,
      noEditable: that.task.taskTypeId?false:true,
      multiple: true,
      addContacts: !(this.isLanding || that.task.taskTypeId ===7),
      required: true,
      typeCall: this.configureTypeCallTaskContact(),
      selectContacts: that.task.participants?that.task.participants:[],
      optionType: that.taskReach.model,
      meetingSaved: that.task.taskId && that.task.taskTypeId === 4?true:false,
      label: this.translate.instant('common.participant')
    }
  }

  configureTypeCallTaskContact(){
    if(this.taskForm.taskReach.model === 'process-task'){
      return 'users';
    }else if(this.taskForm.task?.taskTypeId === 4 || (this.taskForm.task?.taskTypeId === 5 && this.taskForm.concept?.conceptNamedKey !== 'SujetoObligado')){
      return 'groups';
    }else{
      return 'contacts';
    }
  }

  configureFields() {
    let that:any = this.taskForm;
    this.configureFieldsRadioOptions();
    if (that.task?.taskId) {
      that.typeOfTask.noEditable = true;
      that.typeOfTask.disabled = true;
      that.taskExpirationDate.showAlertButton = true;
      that.securitySettings.noEditable = true;
      if(this.genericTask){
        that.taskEvaluation.noEditable = true;
        that.taskClient.noEditable = true;
      }
      if (that.newValueId) {
        that.taskReach.noEditable = true;
      }
      this.configureTaskIdSelected(that.task.taskTypeId);
    }else if (!that.task.taskId) {
      // if(that.task.taskTypeId === 3){
      //   that.typeOfTask.noEditable = this.disabledTaskEvaluation;
      // }
      that.taskExpirationDate.showAlertButton = false;
      this.configureTaskIdSelected(that.task.taskTypeId);
    }
    if (this.fromToConfiguration?.type === 'createEvaluationTask') {
      that.typeOfTask.noEditable = true;
    }
    this.configureFieldsWithTask();
  }

  configureFieldsRadioOptions(){
    let that: any = this.taskForm;
    that.taskReach.canCreate = this.taskForm.canCreate;
    that.securitySettings.canCreate = this.taskForm.canCreate;
    that.listOptionsSecuritySettings = [
      {
        id: 'task-edit-security-settings-open',
        name: this.translate.instant('task-form.open-meeting'),
        value: 'Open'
      }
    ];
  }

  configureTaskIdSelected(newValue:any) {
    // if (newValue === 2) {
    //   this.taskForm.titleParticipants = 'sign';
    //   this.taskForm.taskParticipants.typeConfiguration = this.taskForm.titleParticipants;
    // } else
    if (newValue === 4) {
      this.taskForm.titleParticipants = 'meeting';
      this.taskForm.taskParticipants.typeConfiguration = this.taskForm.titleParticipants;
      setTimeout(()=>{
        this.fieldTypeDateComponent.fieldTypeDate.titleField = this.translate.instant('task-edit.meeting-date');
      })
      // this.taskForm.taskExpirationDate.label = this.translate.instant('task-edit.meeting-date');
      this.taskForm.taskExpirationDate.required = true;
    } else if (newValue === 5) {
      setTimeout(()=>{
        this.fieldTypeDateComponent.fieldTypeDate.titleField = this.translate.instant('task-edit.communication-date');
      })
      // this.taskForm.taskExpirationDate.label = this.translate.instant('task-edit.communication-date');
      this.taskForm.taskExpirationDate.required = true;
    } else {
      setTimeout(()=>{
        this.fieldTypeDateComponent.fieldTypeDate.titleField = this.translate.instant('task-edit.expiration-date');
      })
      // this.taskForm.taskExpirationDate.label = this.translate.instant('task-edit.expiration-date');
      this.taskForm.taskExpirationDate.required = false;
      this.taskForm.taskParticipants.typeConfiguration = this.taskForm.taskReach.model;
    }
    this.taskForm.task.taskTypeId = newValue;
  }

  configureFieldsWithTask(){
    let that:any = this.taskForm;
    if(!this.taskForm.canCreate || (that.task && (that.task.taskStateId === 5 || that.task.taskStateId === 6))){
      this.changeFieldToEditable(['taskTitle', 'taskExpirationDate', 'taskParticipants', 'taskEvaluation', 'taskClient', 'taskReach', 'typeOfTask', 'taskDescription', 'stateStartTime', 'stateEndTime', 'taskPlace', 'typeOfTask','shareTypeDocumentField', 'shareDocumentationField'], true);
      this.initTinyMce(true);
      that.showAccept = (that.task.taskStateId === 5 && !that.task.repeatPatternReadonly && !this.isLanding && that.task.repeatPattern !== null);
      that.repeatPeriod.disabled = true;
    }else{
      switch(that.task.taskTypeId){
        case 1:
        case 6:
          if(this.isLanding){
            this.changeFieldToEditable(['taskTitle', 'taskExpirationDate', 'taskParticipants', 'taskEvaluation', 'taskClient', 'taskReach', 'typeOfTask'], true)
            that.showAccept = true;
          }
          break;
        case 3:
          if(this.isLanding){
            this.changeFieldToEditable(['taskTitle', 'taskExpirationDate', 'taskParticipants', 'taskEvaluation', 'taskClient', 'taskReach', 'typeOfTask'], true);
            that.showAccept = true;
          }else{
            if(!that.task.taskId){
              that.showAccept = true;
            }else if (that.task.taskStateId === 4){
              if(that.task.createdUserId === this.context.user.userId){
                this.changeFieldToEditable(['taskTitle', 'taskEvaluation', 'taskClient', 'taskReach', 'typeOfTask'], true);
              }else{
                this.changeFieldToEditable(['taskTitle', 'taskExpirationDate', 'taskParticipants', 'taskEvaluation', 'taskClient', 'taskReach', 'typeOfTask'], true);
              }
              that.showAccept = true;
            }else{
              if(that.task.createdUserId === this.context.user.userId){
                this.changeFieldToEditable(['taskEvaluation', 'taskClient', 'taskReach', 'typeOfTask'], true);
                that.showAccept = true;
              }else{
                this.changeFieldToEditable(['taskTitle', 'taskExpirationDate', 'taskParticipants', 'taskEvaluation', 'taskClient', 'taskReach', 'typeOfTask', 'taskDescription'], true);
                that.showAccept = false;
              }
            }
          }
          break;
        case 5:
          if(this.isLanding){
            if(that.task.createdUserId !== this.context.user.userId){
              this.changeFieldToEditable(['taskTitle', 'taskExpirationDate', 'taskParticipants', 'taskEvaluation', 'taskClient', 'taskReach', 'taskDescription', 'typeOfTask'], true);
              this.initTinyMce(true);
              that.showAccept = false;
            }
          }else{
            if(moment(that.task.expirationDate).format('DD/MM/YYYY').valueOf() < moment().format('DD/MM/YYYY').valueOf()){
              this.changeFieldToEditable(['taskTitle', 'taskExpirationDate', 'taskParticipants', 'taskEvaluation', 'taskClient', 'taskReach', 'taskDescription', 'typeOfTask'], true);
              this.initTinyMce(true);
              that.showAccept = Boolean(!that.task.repeatPatternReadonly);
            }
          }
          break;
      }
    }
  }

  changeFieldToEditable(listFields:any, value:any) {
    for(let i = 0; i < listFields.length; i++) {
      let field = this.taskForm[listFields[i]];
      field.noEditable = value;
      // field.required = !value;
    }
  }

  getTaskTypes() {
    this.apiService.get('tasks/types')
      .then((data:any)=>{
        //No se pueden crear tareas de proceso
        remove(data, { taskTypeId: 7 });
        this.configureTypes(data);
        this.taskForm.typeList = data;
        if(this.genericTask){
          for (let i = 0; i < this.taskForm.typeList.length; i++) {
            if(this.taskForm.typeList[i].taskTypeId === 3){
              this.taskForm.typeList.splice(i, 1);
              break;
            }
          }
        }else if(this.concept?.hasOpenEvaluationTask || this.fromToConfiguration?.type === 'createFieldTask'){
          for (let i = 0; i < this.taskForm.typeList.length; i++) {
            if(this.taskForm.typeList[i].taskTypeId === 3){
              this.taskForm.typeList[i].disabled = true;
              break;
            }
          }
        }
      }, (errorData:any)=> {
        this.taskForm.typeList = [];
      });
  }

  configureTypes(list:any) {
    list.forEach((item:any) => {
      item.text = item.taskTypeName;
      item.listValueId = item.taskTypeId;
      if (this.taskForm.task.type && item.taskTypeName === this.taskForm.task.type) {
        item.taskTypeName = this.taskForm.task.type;
        this.taskForm.task.taskTypeId = item.listValueId;
      }
    });
  }

  getApiClients(){
    this.apiService.get('clients/root/true/read')
      .then((data:any) => {
        this.getClients(data);
      },(errorData:any)=>{})
  }

  getClients(data: any) {
    let that: any = this.taskForm;
    that.clientListComplete = false;
    that.clients = [];
    that.allClients = [];
    that.filialsCount = 0;
    that.filialsResolvedCount = 0;
    that.finishedGetClients = false;
    data.forEach((client:any, index: any) => {
      if(data.length - 1 === index){
        that.finishedGetClients = true;
      }
      if (client.active) {
        client.text = client.name;
        client.listValueId = client.clientId;
        that.allClients.push(client);
        if(client.hasSubsidiaries){
          that.filialsCount++;
          this.getFilials(client);
        }else{
          that.clients.push(client);
          if(that.finishedGetClients && that.filialsResolvedCount === that.filialsCount){
            this.taskForm.clientListComplete = true;
          }
        }
      }else if(that.finishedGetClients && that.filialsResolvedCount === that.filialsCount){
        this.taskForm.clientListComplete = true;
      }
    })
  }

  getFilials(client:any){
    this.apiService.get('clients/affiliatedcompaniestree', client.clientId)
      .then((data:any) => {
        client.children = data[0].children;
        this.configureChildrenName(client.children);
        this.taskForm.clients.push(client);
        this.taskForm.filialsResolvedCount++;
        if(this.taskForm.finishedGetClients && this.taskForm.filialsResolvedCount === this.taskForm.filialsCount){
          this.taskForm.clientListComplete = true;
        }
      },(errorData:any)=>{})
  }

  configureChildrenName(children:any){
    children.forEach((child:any) => {
      child.text = child.name;
      child.listValueId = child.clientId;
      this.taskForm.allClients.push(child);
      if(child.children.length){
        this.configureChildrenName(child.children);
      }
    });
  }

  getApiEvaluations(){
    this.apiService.get('company', 'dashboard/evaluations')
      .then((data:any) => {
        this.getEvaluations(data);
      },(errorData:any)=>{})
  }

  getEvaluations(evaluations:any) {
    let settings: any = this.context.settings;
    if(this.skin === 'steel-blue' || this.taskForm.task.taskTypeId === 4){
      this.taskForm.evaluations = this.commonUtilsService.editList(evaluations, 'evaluationId', 'name');
    }else{
      let productIdSelected = '';
      if(this.taskForm.task && this.taskForm.task.taskEvaluationId){
        let evaluationSelected = this.searchProductEvaluation(evaluations, this.taskForm.task.taskEvaluationId);
        productIdSelected = evaluationSelected.productId;
        settings.defaultProduct = evaluationSelected.productId;
        settings.defaultProductName = evaluationSelected.productName;
        settings.productWithSnippets = evaluationSelected.snippets;
        this.context.storeSettings();
      }else if(this.genericTask && this.skin === 'icam-red'){
        this.taskForm.task.evaluationId = evaluations[0].evaluationId;
        this.taskForm.newValueId = this.taskForm.task.evaluationId;
      }else if(this.context && settings && settings.defaultProduct){
        productIdSelected = settings.defaultProduct;
      }
      this.taskForm.evaluations = this.getEvaluationsForProduct(evaluations, productIdSelected);
    }
  }

  getEvaluationsForProduct(data:any, productIdSelected:any){
    let evaluationsList:any = [];
    data.forEach((evaluation:any)=>{
      evaluation.text = evaluation.name;
      evaluation.listValueId = evaluation.evaluationId;
      if(productIdSelected === evaluation.productId){
        evaluationsList.push(evaluation);
      }
    })
    return evaluationsList;
  }

  searchProductEvaluation(data:any, taskEvaluationId:any){
    for (let i = 0; i < data.length; i++) {
      let evaluation:any = data[i];
      if(taskEvaluationId === evaluation.evaluationId){
        return evaluation;
      }
    }
  }

  getPatterns(){
    this.taskForm.patternList = pattern;
    this.configurePatternList();
    if(this.taskForm.task.repeatPattern && this.taskForm.task.repeatPattern.frecuencyId){
      this.taskForm.repetitionActived = true;
      this.configureRepeatPattern();
    }
  }

  configurePatternList(){
    let that: any = this.taskForm;
    that.patternList.weekDays.forEach((weekDays:any) => {
      // weekDays.text = moment().weekday(weekDays.value).format('ddd')
      weekDays.text = moment().weekday(weekDays.value).format('dd')
    });
    that.patternList.monthList.forEach((monthList:any) => {
      monthList.text = moment().month(monthList.value).format('MMMM')
    });
    that.patternList.periodicityList.forEach((periodicityList:any) => {
      periodicityList.text = this.translate.instant(periodicityList.text)
    });
    that.patternList.numeralTimeList.forEach((numeralTimeList:any) => {
      numeralTimeList.text = this.translate.instant(numeralTimeList.text)
    });
    that.patternList.weekDayList.forEach((weekDayList:any) => {
      weekDayList.text = weekDayList.id < 10? moment().weekday(weekDayList.value).format('dddd') : this.translate.instant(weekDayList.text);
    });
  }

  configureRepeatPattern(){
    let task:any = this.taskForm.task;
    this.taskForm.repeatPattern = {
      frecuency: this.commonUtilsService.extractToArray(task.repeatPattern.frecuencyId, this.taskForm.patternList.periodicityList, 'listValueId').text,
      startInterval: task.repeatPattern.repeatStart
    }
    this.taskForm.repeatPattern.selectInterval = this.getSelectInterval();
    this.taskForm.repeatPattern.finishInterval = this.extractFinishInterval(this.taskForm.repeatPattern.selectInterval, task.repeatPattern.repeatUntil, task.repeatPattern.repeatTimes);
    this.taskForm.repeatPattern.momentOfCreation = this.setMomentCreation(task.repeatPattern.frecuencyId, task.repeatPattern.repeatOption, task.repeatPattern)
  }

  getSelectInterval(){
    let task: any = this.taskForm.task;
    if(task.repeatPattern.repeatTimes !== null){
      return 'finish-after';
    }else if(task.repeatPattern.repeatUntil !== null){
      return 'end-interval-day';
    }else{
      return 'no-end-date';
    }
  }

  extractFinishInterval(selectInterval:string, repeatUntil:any, repeatTimes:any){
    switch(selectInterval){
      case 'no-end-date':
        return ''
      case 'finish-after':
        this.taskForm.task.repeatPattern.repeatTimes = repeatTimes;
        return repeatTimes +' '+ this.translate.instant('repeat-pattern.repetitions');
      case 'end-interval-day':
        this.taskForm.task.repeatPattern.repeatUntil = repeatUntil;
        return typeof repeatUntil === 'string'?repeatUntil : this.dateConverterService.toIso(repeatUntil);
      default:
        return '';
    }
  }

  setMomentCreation(frecuency:any, repeatOption:any, params:any){
    let that: any = this.taskForm;
    let task: any = this.taskForm.task;
    switch(frecuency){
      case 1:
        if(repeatOption === 1){
          task.repeatPattern.repeatParam1 = params.repeatParam1;
          return this.translate.instant('add-alert.every')+" "+
            params.repeatParam1 +" "+
            this.translate.instant('repeat-pattern.days');
        }else{
          return this.translate.instant('repeat-pattern.all-days-week');
        }
      case 2:
        let weekSelect = this.extractWeekSelect(params.repeatParam2);
        task.repeatPattern.repeatParam1 = params.repeatParam1;
        task.repeatPattern.repeatParam2 = params.repeatParam2;
        return this.translate.instant('repeat-pattern.repeat-every')+" "+
          params.repeatParam1 +" "+
          this.translate.instant('repeat-pattern.weeks-in-days') +" "+
          weekSelect;
      case 3:
        if(repeatOption === 1){
          task.repeatPattern.repeatParam1 = params.repeatParam1;
          task.repeatPattern.repeatParam2 = params.repeatParam2;
          return this.translate.instant('repeat-pattern.the-day') +" "+
            params.repeatParam1 +" "+
            this.translate.instant('repeat-pattern.kind') +" "+
            params.repeatParam2 +" "+
            this.translate.instant('repeat-pattern.month');
        }else{
          task.repeatPattern.repeatParam1 = params.repeatParam1
          task.repeatPattern.repeatParam2 = params.repeatParam2
          task.repeatPattern.repeatParam3 = params.repeatParam3
          return this.translate.instant('repeat-pattern.the') +" "+
            this.commonUtilsService.extractToArray(params.repeatParam1, that.patternList.numeralTimeList, 'id').text +" "+
            this.commonUtilsService.extractToArray(params.repeatParam2, that.patternList.weekDayList, 'id').text +" "+
            this.translate.instant('repeat-pattern.kind') +" "+ params.repeatParam3 +" "+ this.translate.instant('repeat-pattern.month');
        }
      default:
        if(repeatOption === 1){
          task.repeatPattern.repeatParam1 = params.repeatParam1;
          task.repeatPattern.repeatParam2 = params.repeatParam2;
          return this.translate.instant('repeat-pattern.the') +" "+
            params.repeatParam1 +" "+
            this.translate.instant('repeat-pattern.kind') +" "+
            this.commonUtilsService.extractToArray(params.repeatParam2, that.patternList.monthList, 'id').text;
        }else{
          task.repeatPattern.repeatParam1 = params.repeatParam1
          task.repeatPattern.repeatParam2 = params.repeatParam2
          task.repeatPattern.repeatParam3 = params.repeatParam3
          return this.translate.instant('repeat-pattern.the') +" "+
            this.commonUtilsService.extractToArray(params.repeatParam1, that.patternList.numeralTimeList, 'id').text +" "+
            this.commonUtilsService.extractToArray(params.repeatParam2, that.patternList.weekDayList, 'id').text +" "+
            this.translate.instant('repeat-pattern.kind') +" "+
            this.commonUtilsService.extractToArray(params.repeatParam3, that.patternList.monthList, 'id').text;
        }
    }
  }

  extractWeekSelect(weekdayMaskEnum:any){
    let weekDays = '';
    this.taskForm.patternList.weekDays.forEach((weekdays:any) => {
      if((weekdays.bit && weekdayMaskEnum) === weekdays.bit){
        weekDays += moment().weekday(weekdays.value).format('dddd')+', '
      }
    });
    return weekDays.substring(0 , weekDays.length - 2);
  }

  configureShareDocuments(){
    let that: any = this.taskForm;
    this.checkHasContacts();
    if(that.task.sharedDocumentObjectsInEvaluationTask){
      that.shareDocumentationListSelected = this.extractSharedDocumentsIds()
    }
    if(that.task.documentsShareType){
      that.typeShareDocumentSelected = String(that.task.documentsShareType);
    }
  }

  checkHasContacts(){
    this.taskForm.hasContacts = Boolean(this.taskForm.task.participants.filter((contact:any)=>{
      return contact.type ==='C';
    }).length)
  }

  extractSharedDocumentsIds(){
    let that: any = this.taskForm;
    let list: string = '';
    that.task.sharedDocumentObjectsInEvaluationTask.forEach((element:any) => {
      list += '|'+ element.documentObjectId
    });
    return list.substring(1, list.length)
  }

  cancelCreateTask(){
    this.cancelTask.emit();
  }

  resetCallContacts(){
    delete this.taskForm.resetCallsInput;
  }

  receiveContacts(e:any) {
    this.itemSelected(e.contacts, 'participants');
  }

  itemSelectedOn(e:any, type:any ) {
    this.itemSelected(e.newValue, type);
  }

  itemSelected(newValue:any, type:string) {
    let that: any = this.taskForm;
    let previousValue: any = that.task[type];
    let changeField: boolean = false;
    switch (type) {
      case 'expirationDate':
        this.configureTimeSelect(newValue);
        break;
      case 'taskTypeId':
        if(that.task.taskTypeId !== newValue){
          that.task.repeatPattern = null;
          that.repetitionActived = false;
          that.task.type = this.getTitleText(that.task);
          this.taskForm.taskParticipants.noEditable = newValue?false:true;
        }
        that.titleParticipants = 'task';
        this.configureTaskIdSelected(newValue);
        if(newValue){
          let typeCall: any = this.taskForm.taskParticipants.typeCall;
          this.taskForm.taskParticipants.typeCall = this.configureTypeCallTaskContact();
          if(typeCall === 'groups' && this.taskForm.taskParticipants.typeCall !== 'groups'){
            this.taskForm.task.participants = this.taskForm.task.participants.filter((contact:any)=>{
              return contact.type !=='G'
            })
            this.taskForm.taskParticipants.selectContacts = this.taskForm.task.participants;
          }
          this.taskForm.resetCallsInput = typeCall !== this.taskForm.taskParticipants.typeCall?true:undefined;
        }else{
          that.task.participants = [];
          this.taskForm.taskParticipants.selectContacts = this.taskForm.task.participants;
          this.taskForm.resetCallsInput = true;
        }
        break;
      case 'taskClientId':
        previousValue = that.task.clientId;
        that.newValueId = newValue;
        that.task.clientId = newValue;
        that.task.evaluationId = null;
        break;
      case 'taskEvaluationId':
        previousValue = that.task.evaluationId;
        that.newValueId = newValue;
        that.task.evaluationId = newValue;
        that.task.clientId = null;
        break;
      case 'reach':
        previousValue = that.taskReach.model;
        delete that.newValueId;
        delete that.task.taskTypeId;
        that.taskReach.model = newValue;
        that.taskParticipants.typeConfiguration = newValue;
        that.taskParticipants.optionType = newValue;
        break;
      case 'endHour':
        that.task.endHour = newValue;
        if(newValue){
          delete that.stateEndTime.errorText;
        }
        break;
      case 'repeatPeriod':
        previousValue = that.repetitionActived;
        this.setPattern(newValue);
        break;
      case 'description':
        that.task.description = newValue;
        break;
      case 'shareDocumentation':
        previousValue = that.shareDocumentationListSelected;
        that.shareDocumentationListSelected = newValue;
        break;
      case 'participants':
        changeField = true;
        that.task.participants = newValue;
        this.checkHasContacts();
        break;
      default:
        that.task[type] = newValue;
        break;
    }
    if(newValue !== previousValue || changeField){
      this.conceptState.addToTask(type);
      if(type === 'reach'){
        this.conceptState.addToTask('taskTypeId');
        this.conceptState.addToTask('taskEvaluationId');
        this.conceptState.addToTask('taskClientId');
      }else if(type === 'taskTypeId' && !newValue){
        this.conceptState.addToTask('participants');
      }
    }
  }

  configureTimeSelect(newValue:any) {
    this.taskForm.task.expirationDate = newValue;
    if (newValue !== null && newValue !== undefined) {
      this.taskForm.task.expirationDate = this.dateConverterService.toIso(newValue);
    }else if (newValue === null){
      this.taskForm.task.expirationDate = '';
      this.taskForm.task.repeatPattern = null;
      this.taskForm.repetitionActived = false;
      this.taskForm.task.type = this.getTitleText(this.taskForm.task)
    }
    if(this.taskForm.task.repeatPattern && this.taskForm.task.repeatPattern.repeatStart && this.taskForm.task.taskTypeId === 5){
      this.taskForm.task.repeatPattern.repeatStart = this.dateConverterService.toIso(newValue);
      this.taskForm.repeatPattern.startInterval = this.dateConverterService.toIso(newValue);
      this.getNextRepeat();
    }
  }

  getTitleText(task:any){
    switch(task.taskTypeId){
      case 1:
        return task.repeatPattern && task.repeatPattern.frecuencyId? this.translate.instant('task-edit.periodic-info-request') : this.translate.instant('task-edit.periodic-request');
      case 3:
        return task.repeatPattern && task.repeatPattern.frecuencyId? this.translate.instant('task-edit.periodic-evaluation') : this.translate.instant('common.evaluation');
      case 5:
        return task.repeatPattern && task.repeatPattern.frecuencyId? this.translate.instant('task-edit.periodic-comunication') : this.translate.instant('law-info.comm');
      case 6:
        return task.repeatPattern && task.repeatPattern.frecuencyId? this.translate.instant('task-edit.periodic-management') : this.translate.instant('task-edit.generic-task');
      default:
        return task.type;
    }
  }

  getNextRepeat(){
    this.apiService.add('repeatpattern/next-repeat', this.taskForm.task.repeatPattern)
      .then((data:any)=>{
        this.taskForm.task.repeatPattern.nextRepeat = data.nextRepeat;
      }, (errorData:any)=>{})
  }

  addOrEditTask() {
    let that: any = this.taskForm;
    let type: string = 'saveTask';
    if (that.task.taskStateId === 5 && !that.task.repeatPatternReadonly && that.modifyRepeatPattern){
      let task:any = {
        taskTypeId: that.task.taskTypeId,
        title: that.task.title,
        description: that.task.description,
        participants: that.task.participants,
        expirationDate: that.task.expirationDate,
        taskId: that.task.taskId,
        location: that.task.location,
        beginHour: that.task.beginHour,
        endHour: that.task.endHour,
        repeatPattern: that.task.repeatPattern,
        url: that.task.url,
        password: that.task.password
      };
      that.modifyRepeatPattern = false;
      this.emitSaveTask(task, type);
    }else if (this.validateFieldsForm()) {
      let task = that.task;
      if (that.task.taskId) {
        task = {
          taskTypeId: that.task.taskTypeId,
          title: that.task.title,
          description: that.task.description,
          participants: that.task.taskId? this.configureParticipants(that.task.participants) : that.task.participants,
          expirationDate: that.task.expirationDate,
          taskId: that.task.taskId,
          location: that.task.location,
          beginHour: that.task.beginHour,
          endHour: that.task.endHour,
          repeatPattern: that.task.repeatPattern,
          url: that.task.url,
          password: that.task.password,
          virtualRoomWithPassword: that.task.virtualRoomWithPassword
        };
      } else{
        type = 'createTask';
      }
      if(that.task.taskTypeId === 3 && that.hasContacts){
        task.documentsShareType = that.typeShareDocumentSelected;
        if(task.documentsShareType === '2'){
          if(that.task.taskId){
            task.sharedDocumentObjectsInEvaluationTask = this.configureSharedDocumentsInTask();
          }else{
            task.sharedDocumentObjects = this.extractDocumentObjects();
          }
        }
      }
      this.emitSaveTask(task, type);
    }else{
      delete this.taskForm.configurationFromExitModal;
      this.modalService.errorModal({ text: this.translate.instant('task-form.error-validate-task') });
    }
    if(type === 'createTask'){
      this.getApiEvaluations();
    }
  }

  emitSaveTask(task: any, type: any){
    this.launchAction.emit({
      action:{
        type: type,
        task: task,
        fromExitModal: this.taskForm.configurationFromExitModal?.fromExitModal?true:false,
        configurationFromExitModal: this.taskForm.configurationFromExitModal
      }
    });
    delete this.taskForm.configurationFromExitModal;
    this.conceptState.removeAllTasks();
  }

  validateFieldsForm() {
    let that: any = this.taskForm;
    let task: any = that.task;
    that.taskExpirationDate.error = false;
    that.taskTitle.error = task.title === '' || !task.title;
    that.genericError = false;
    that.errorDate = false;
    if(this.checkParticipants()){
      that.taskParticipants.error = true;
      this.modalService.errorModal({ text: this.translate.instant('task-form.error-participants') });
      return false;
    }

    if (this.genericTask && this.skin !== 'icam-red'){
      this.validateGeneric();
    }

    if(!(task.taskTypeId === null || task.taskTypeId === undefined || task.taskTypeId === '')){
      that.typeOfTask.error = false;
      switch(task.taskTypeId){
        case 5:
          this.checkTaskExpirationDateError(task);
          that.taskParticipants.error = task.participants && task.participants.length < 1;
          return !(that.taskParticipants.error || that.genericError || that.taskTitle.error || that.taskExpirationDate.error);
        case 4:
          this.checkTaskExpirationDateError(task);
          that.taskParticipants.error = task.participants && task.participants.length < 1;
          that.stateStartTime.error = !task.beginHour;
          that.stateEndTime.error = !task.endHour || task.beginHour >= task.endHour;
          if(task.endHour && task.beginHour >= task.endHour){
            that.stateEndTime.errorText = this.translate.instant('task-form.error-second-hour');
          }
          return !(that.taskTitle.error || that.taskPlace.error ||  that.taskExpirationDate.error || that.stateStartTime.error || that.stateEndTime.error || that.taskParticipants.error || that.genericError);
        // case 2:
        //   that.taskParticipants.error = task.participants && task.participants.length < 1;
        //   return !(that.taskParticipants.error || that.taskTitle.error || that.genericError);
        case 1:
        case 6:
          this.checkTaskExpirationDateError(task);
          that.taskParticipants.error = task.participants && task.participants.length < 1;
          return !(that.taskParticipants.error || that.taskTitle.error || that.genericError || that.taskExpirationDate.error);
        case 3:
          that.shareDocumentationField.error = false;
          this.checkTaskExpirationDateError(task);
          that.taskParticipants.error = task.participants && task.participants.length < 1;
          that.shareDocumentationField.error = Boolean(that.hasContacts && that.typeShareDocumentSelected==='2' && (!that.shareDocumentationListSelected || that.shareDocumentationListSelected === ''));
          return !(that.taskParticipants.error || that.taskTitle.error || that.genericError || that.taskExpirationDate.error || that.shareDocumentationField.error);
        default:
          that.taskParticipants.error = task.participants && task.participants.length < 1;
          return !(that.taskParticipants.error || that.taskTitle.error || that.genericError);
      }
    }else{
      that.typeOfTask.error = true;
      // that.taskExpirationDate.error = !validDate;
      return false;
    }
  }

  checkTaskExpirationDateError(task: any){
    let that: any = this.taskForm;
    this.fieldTypeDateComponent.checkDateLock();
    that.taskExpirationDate.error = that.taskExpirationDate.error || (that.taskExpirationDate.required && !task.expirationDate) || that.taskExpirationDate.errorInDateLock;
  }

  checkParticipants(){
    for(let i = 0; i < this.taskForm.task.participants.length; i++){
      if(this.taskForm.task.participants[i].isDeleted){
        return true;
      }
    }
    return false;
  }

  validateGeneric() {
    if(this.taskForm.taskReach.model){
      if (this.taskForm.taskReach.model === 'Evaluation') {
        this.taskForm.taskEvaluation.error = this.taskForm.task.evaluationId === '' || !this.taskForm.task.evaluationId;
        this.taskForm.genericError = this.taskForm.taskEvaluation.error;
      } else {
        this.taskForm.taskClient.error = this.taskForm.task.clientId === '' || !this.taskForm.task.clientId;
        this.taskForm.genericError = this.taskForm.taskEvaluation.error;
      }
    }else{
      this.taskForm.taskReach.error = true;
      this.taskForm.genericError = false;
    }
  }

  configureParticipants(participants:any){
    participants.forEach((participant:any) => {
      if(participant.type === 'U' && !participant.userId){
        participant.userId = participant.taskParticipantId;
      }else if(participant.type === 'C' && !participant.contactId){
        participant.contactId = participant.taskParticipantId;
      }else if(participant.type === 'G' && !participant.groupId){
        participant.groupId = participant.taskParticipantId;
      }else if(!participant.productRoleId){
        participant.productRoleId = participant.taskParticipantId;
      }
    });
    return participants;
  }

  configureSharedDocumentsInTask(){
    let list: any = [];
    let documentsSelected: any = this.taskForm.shareDocumentationListSelected.indexOf('|') === -1? [this.taskForm.shareDocumentationListSelected] : this.taskForm.shareDocumentationListSelected.split('|');
    documentsSelected.forEach((id:any) => {
      this.taskForm.shareDocumentationList.forEach((document:any)=>{
        if(document.documentObjectId === parseInt(id)){
          list.push({
            tasktDocumentObjectId: this.findInShared(document.documentObjectId),
            taskId: this.task.taskId,
            documentObjectId:document.documentObjectId,
          })
        }
      })
    });
    return list;
  }

  findInShared(documentObjectid:any){
    let document: any = this.taskForm.task.sharedDocumentObjectsInEvaluationTask.filter((document:any)=>{
      if(document.documentObjectId === documentObjectid){
        return document;
      }
    })[0];

    return document? document.tasktDocumentObjectId: 0;
  }

  extractDocumentObjects(){
    let arrayDocuments:any = [];
    let documentsSelected: any = this.taskForm.shareDocumentationListSelected.indexOf('|') === -1? [this.taskForm.shareDocumentationListSelected] : this.taskForm.shareDocumentationListSelected.split('|');
    documentsSelected.forEach((document:any)=>{
      arrayDocuments.push(parseInt(document))
    })
    return arrayDocuments;
  }

  changeValueOption(e:any){
    let that: any = this.taskForm;
    switch(e.option.type){
      case 'securitysettings':
        that.task.virtualRoomWithPassword = false;
        if(that.securitySettings.model === 'Private'){
          that.task.virtualRoomWithPassword = true;
        }
        break;
      default:
        break;
    }
  }

  createTaskAlert(e:any){
    e.alert.nameField = this.taskForm.taskExpirationDate.label;
    this.createAlert.emit({alert:e.alert});
  }

  setPattern(newValue:boolean){
    let date:any = null;
    if(this.taskForm.showAccept){
      if(this.taskForm.task.taskTypeId === 5){
        date = this.taskForm.task.expirationDate;
      }
      if(!this.taskForm.repetitionActived){
        this.modifyPattern(date);
      }else{
        this.taskForm.task.repeatPattern = null;
        this.taskForm.task.type = this.getTitleText(this.taskForm.task);
        this.taskForm.repetitionActived = newValue;
        this.taskForm.modifyRepeatPattern = true;
      }
    }
  }

  modifyPattern(date:any = null){
    let task:any = this.taskForm.task;
    if(!task.repeatPatternReadonly && !this.isLanding && this.userPermissions !=='none'){
      if(task.taskTypeId === 5){
        if(task.expirationDate){
          this.launchSetPattern(task.expirationDate);
        }
      }else{
        this.launchSetPattern(date);
      }
      this.taskForm.modifyRepeatPattern = true;
    }
  }

  launchSetPattern(date:any){
    this.modalService.establishPattern({patternList: this.taskForm.patternList, item: this.taskForm.task, date: date }).subscribe((result:any)=>{
      if(result.result === 'ok'){
        this.configureTaskRepeat(result);
        this.getNextRepeat();
        this.taskForm.task.type = this.getTitleText(this.taskForm.task);
      }else if(result.result === 'delete'){
        this.taskForm.task.repeatPattern = null
        this.taskForm.repetitionActived = !this.taskForm.repetitionActived;
        this.taskForm.task.type = this.getTitleText(this.taskForm.task);
      }
    })
  }

  configureTaskRepeat(result:any){
    this.taskForm.repetitionActived = true;
    this.taskForm.task.repeatPattern = {
      frecuencyId: result.frecuency.listValueId,
      repeatStart: this.dateConverterService.toIso(result.interval.startInterval),
      repeatOption: result.repetition.repeatOption,
      repeatTimes: null,
      repeatUntil: null,
      repeatParam1: null,
      repeatParam2: null,
      repeatParam3: null
    }

    this.taskForm.repeatPattern = {
      frecuency: result.frecuency.text,
      startInterval: result.interval.startInterval,
      finishInterval: this.extractFinishInterval(result.interval.selectInterval, result.interval.finishIntervalDate, result.interval.repeatTimes),
      selectInterval: result.interval.selectInterval
    }
    this.taskForm.repeatPattern.momentOfCreation = this.setMomentCreation(this.taskForm.task.repeatPattern.frecuencyId, this.taskForm.task.repeatPattern.repeatOption, result.repetition.repeatParams);
  }

  cancelMeeting(){
    this.modalService.infoModal({ text: this.translate.instant('task-form.cancel-meeting-confirm'),typeButtons:'desconveneCancel' })
      .subscribe((result:any)=>{
        if(result.result ==='ok'){
          this.closeMeeting();
        }
      })
  }

  closeMeeting(){
    let taskCopy = cloneDeep(this.taskForm.task);
    taskCopy.taskStateId = 5;
    taskCopy.client = this.taskForm.taskClient
    taskCopy.createdBy = this.taskForm.task.createdUserId;
    taskCopy.evaluation = this.taskForm.evaluations.find((evaluation:any) => evaluation.evaluationId = this.taskForm.task.evaluationId)
    this.apiService.patch('tasks/changestate', this.taskForm.task.taskId, taskCopy)
      .then(()=>{
          this.cancelCreateMeetingTask();
      }, (error:any)=> {});
  }

  cancelCreateMeetingTask(){
    this.taskForm.task.taskStateId = 5;
    this.taskForm.task.state = this.translate.instant('common.closed');
    this.modalService.successModal({ text: this.translate.instant('task-form.cancel-meeting-ok') })
      .subscribe((result:any)=>{
          this.cancelTask.emit();
      })
  }

  copyText(type:any){
    this.commonUtilsService.clipboardText(type);
  }

  openUrl(url:string){
    window.open(url, '_blank');
  }

  sendNotify(){
    this.modalService.sendReminder(this.taskForm.task)
      .subscribe((result:any)=>{
        if(result.result==='ok'){
          this.modalService.successModal({ text: result.text });
        }
      })
  }

  changeCloseTaskState(){
    this.configureFieldsWithTask();
  }

  disableTaskTypeInput(){
    if(this.genericTask){
      this.taskForm.taskReach.noEditable = true;
      this.taskForm.taskEvaluation.noEditable = true;
      this.taskForm.taskClient.noEditable = true;
    }
    this.taskForm.taskExpirationDate.showAlertButton = true;
    for (let i = 0; i < this.taskForm.typeList.length; i++) {
        if (this.taskForm.task.taskTypeId === this.taskForm.typeList[i].taskTypeId) {
            this.taskForm.task.type = this.taskForm.typeList[i].taskTypeName;
            break;
        }
    }
    this.taskForm.typeOfTask.noEditable = true;
    this.taskForm.taskParticipants.noEditable = this.taskForm.task.taskTypeId === 3;
    this.taskForm.securitySettings.noEditable = this.taskForm.task.taskTypeId === 4;
  }

  ngOnDestroy(){
    this.commonUtilsService.OnDestroy(this.subscribers);
  }
}
